/**
 * TableSyncPicker - the shared "choose what to sync" UI used by both the
 * Connect flow and the Add-sync flow. Renders the plan-budget banner, the
 * frequency + import-history controls, and the auto-mapped table cards (each
 * with per-column overrides and an interactive import-scope control).
 */

import React from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Switch, Alert, LinearProgress,
  ToggleButton, ToggleButtonGroup, FormControlLabel, useTheme,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import { FREQUENCIES, totalEstSignals, compactNum } from './connectorMeta';
import TableCard from './TableCard';

export default function TableSyncPicker({
  tables, selected, setSelected, expanded, setExpanded,
  frequency, setFrequency, importHistory, setImportHistory, updateColumn,
  plan, fitToPlan, setFitToPlan, scopes = {}, setScope,
  // REST sources show a dedicated "Data capture" panel (historical + live /
  // live / snapshot) instead of the simple import-history switch.
  hideHistoryToggle = false,
}) {
  const theme = useTheme();
  const card = { border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: theme.palette.background.paper };
  const sectionLabel = { fontSize: 11, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'text.secondary', mb: 1 };

  const remaining = plan?.signals_remaining;
  const limit = plan?.signals_limit;
  const estTotal = totalEstSignals(tables, selected);
  const haveEstimate = estTotal > 0 && remaining != null;
  const overBudget = haveEstimate && importHistory && estTotal > remaining;
  const usedPct = limit ? Math.min(100, Math.round(((plan?.signals_used || 0) / limit) * 100)) : 0;

  return (
    <>
      {haveEstimate && (
        <Paper elevation={0} sx={{ ...card, p: 1.5, mb: 2, borderColor: overBudget ? theme.palette.warning.main : theme.palette.divider }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, flex: 1 }}>
              Estimated <strong>~{compactNum(estTotal)}</strong> signals
              {remaining != null && <> · plan has <strong>{compactNum(remaining)}</strong> of {compactNum(limit)} remaining</>}
            </Typography>
            <Chip size="small" color={overBudget ? 'warning' : 'success'} variant="outlined"
              label={overBudget ? 'Over plan limit' : 'Within plan'} sx={{ height: 20, fontSize: 11 }} />
          </Stack>
          <LinearProgress variant="determinate" value={usedPct} sx={{ height: 6, borderRadius: 1, mb: overBudget ? 1 : 0 }} />
          {overBudget && (
            <Alert severity="warning" sx={{ mt: 1, py: 0.25 }}
              action={
                <FormControlLabel sx={{ mr: 0 }}
                  control={<Switch size="small" checked={!!fitToPlan} onChange={(e) => setFitToPlan?.(e.target.checked)} />}
                  label={<Typography sx={{ fontSize: 12 }}>Fit to plan</Typography>} />
              }>
              <Typography sx={{ fontSize: 12.5 }}>
                This source is larger than your plan. Turn on <strong>Fit to plan</strong> to import only the
                most recent rows that fit, or set a per-table scope below. Otherwise the import stops at the limit.
              </Typography>
            </Alert>
          )}
        </Paper>
      )}

      <Paper elevation={0} sx={{ ...card, p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box>
            <Typography sx={sectionLabel}><ScheduleIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: -2 }} />Sync frequency</Typography>
            <ToggleButtonGroup size="small" exclusive value={frequency} onChange={(_, v) => v && setFrequency(v)} sx={{ flexWrap: 'wrap' }}>
              {FREQUENCIES.map((f) => (
                <ToggleButton key={f.id} value={f.id} sx={{ textTransform: 'none', px: 1.75, py: 0.5, fontSize: 13 }}>{f.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          {!hideHistoryToggle && (
            <FormControlLabel
              control={<Switch checked={importHistory} onChange={(e) => setImportHistory(e.target.checked)} />}
              label={<Stack direction="row" spacing={0.5} alignItems="center"><HistoryIcon fontSize="small" /><span>Import history now</span></Stack>}
            />
          )}
        </Stack>
      </Paper>

      <Stack spacing={1}>
        {tables.length === 0 && (
          <Paper elevation={0} sx={{ ...card, p: 3, textAlign: 'center' }}>
            <Typography color="text.secondary" variant="body2">No tables found in this source.</Typography>
          </Paper>
        )}
        {tables.map((t) => (
          <TableCard
            key={t.table_name}
            table={t}
            selectable
            selected={!!selected[t.table_name]}
            onToggleSelect={(v) => setSelected((s) => ({ ...s, [t.table_name]: v }))}
            expanded={expanded === t.table_name}
            onToggleExpand={() => setExpanded(expanded === t.table_name ? null : t.table_name)}
            updateColumn={(idx, patch) => updateColumn(t.table_name, idx, patch)}
            showScope={importHistory}
            scope={scopes[t.table_name]}
            setScope={(patch) => setScope?.(t.table_name, patch)}
          />
        ))}
      </Stack>
    </>
  );
}
